import { Icon } from '@progress/kendo-react-common';
import { useEffect, useState } from 'react'
import { APISettings } from '../../../config/ApiSettings';
import CustomButton from '../../stateless/customButton/CustomButton';
import { useDropzone } from "react-dropzone";
import { ProgressBar } from '@progress/kendo-react-progressbars';
import { useFileUploadControls } from './FileUploadController';
import './FileUpload.scss'

interface IFileUploadNew {
  containerName: string;
  type: 'image' | 'file';
  disabled?: boolean;
  disablePreview?: boolean;
  existingFiles?: any[];
  saveFile?: (file: any, name?: string) => void;
  deleteFile?: (file: any) => void;
  notification: (message: string, type: 'error' | 'success') => void;
  allowedTypes?: 'image' | 'pdf' | 'text' | 'whitelist';
  required?: boolean;
  useOriginalFileName?: boolean;
}

/**
 *
 * @param containerName string - name of the blob container you want to save files to
 * @param type 'image' | 'file' - changes how the file prievew pane looks 'image' shows image preivews
 * @param disabled boolean - true disables the component so not files can be uploaded but still shows preview
 * @param disablePreview boolean - if you don't want the preview pane to show
 * @param existingFiles [{blobName: string, ...}] - should have at least the blobName property in the file object
 * @param saveFile (file:any) => void - on successful upload this function can be used to associate the files uuid and container in the database
 * @param notification (message:string, type: 'error' | 'success') = void - function to trigger notification display. Leverage the notification helper
 * @param alledTypes 'image' | 'pdf' | 'text' - sets the types of files allowed to be uploaded 'text' = .doc .docx, image = .jpg, .png, pdf = .pdf only
 * @example
 *  <FileUpload
      containerName={BlobContainer.chapterImageContainer}
      type={'image'}
      existingFiles={chapterFiles}
      saveFile={saveFile}
      notification={handleFileUploadResponseMessage}
      allowedTypes='image'
    />
 * @function notification This function example utilizes the notification helper
 * @example
    const handleFileUploadResponseMessage = (message: string, type: 'error' | 'success') => {
      addNotification({
        type: type,
        message: message,
      });
    };
 * @returns React.Component
 */
function FileUpload(props: IFileUploadNew) {

  const {
    // functions
    onDrop,
    copyFileUrl,
    removeFile,
    // state
    filePreviews,
    fileName,
    uploadPercentage,
    fileTypes,
    // props
    containerName,
    disabled,
    disablePreview,
    type,
  } = useFileUploadControls(props);

  const { getRootProps, getInputProps } = useDropzone({
    disabled: !!props.disabled, onDrop, accept: fileTypes
  });

  const baseUrl = new APISettings().getApiURL();

  return (
    <div>
      <div className={'dropZone upload-box'} {...getRootProps()}>
        <div className='inner-dropzone'>
          <input {...getInputProps()} />
          <div className='upload-header'><div>Browse or drop files here</div><div><Icon name='upload' themeColor='primary' /></div></div>
          <>
            <div className={'file-name'}>{fileName}</div>
            <div className='progress-bar'>
              <ProgressBar value={uploadPercentage} progressStyle={{ background: "#8EBC00" }} />
            </div>
          </>
        </div>
        {filePreviews && filePreviews.length < 1 && props.required ? (
          (<div className="file-name k-form-error">This field is required</div>)
        ) : null}
      </div>
      {
        !disablePreview && filePreviews ?
          type === 'image' ? (
            <div className={"img-preview"}>
              <h5 className="img-heading">Images</h5>
              {filePreviews ?
                filePreviews.map((file: any) => (
                  <div className="preview-files" key={file.id}>
                    <img
                      src={`${baseUrl}/fileStorage/GetAsByteArray/${containerName}/${file.blobName}`}
                      alt={file.fileName}
                      style={{ width: 200, margin: 10 }}
                    />
                    <div>
                      <CustomButton
                        onClick={() => { }}
                        size="small"
                        className="download-button"
                      >
                        <a className='hidden-anchor' href={`${baseUrl}/fileStorage/GetAsByteArray/${containerName}/${file.blobName}`}>
                          <Icon name="download" themeColor='light' />
                        </a>
                      </CustomButton>
                      <CustomButton
                        onClick={() => copyFileUrl(`${baseUrl}/fileStorage/GetAsByteArray/${containerName}/${file.blobName}`)}
                        icon="copy"
                        size="small"
                        themeColor="success"
                      />
                      {!disabled ? (
                        <CustomButton
                          themeColor='error'
                          onClick={() => removeFile(file)}
                          icon="delete"
                          size="small"
                        />
                      ) : null}
                    </div>
                  </div>
                )) : null}
            </div>
          ) : (
            <div className={"img-preview"}>
              <h5>Files</h5>
              {filePreviews ?
                filePreviews.map((file: any) => (
                  <div className="preview-files" key={file.id}>
                    <div style={{ width: "80%" }}>{file.fileName ?? file.originalName ?? file.blobName}</div>
                    <div>
                      <CustomButton
                        onClick={() => { }}
                        size="small"
                        className="download-button"
                      >
                        <a className='hidden-anchor' href={`${baseUrl}/fileStorage/GetAsByteArray/${containerName}/${file.blobName}`}><Icon name="download" themeColor='light' /></a>
                      </CustomButton>
                      <CustomButton
                        onClick={() => copyFileUrl(`${baseUrl}/fileStorage/GetAsByteArray/${containerName}/${file.blobName}`)}
                        icon="copy"
                        size="small"
                        themeColor="success"
                      />
                      {!disabled ? (
                        <CustomButton
                          themeColor='error'
                          onClick={() => removeFile(file)}
                          icon="delete"
                          size="small"
                        />
                      ) : null}
                    </div>
                  </div>
                )) : null}
            </div>
          )
          : null
      }
    </div >
  )
}

export default FileUpload